import {Component, Input, OnDestroy, OnInit, Optional, ViewChild} from '@angular/core';
import {NgModel} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {Subscription} from 'rxjs';
import {countryNames, fieldError, fieldErrors, InvoicingService} from '../../shared/invoicing.service';
import {Moment} from 'moment';
import {Globals} from '../../shared/Globals';
import {BlockingDraft, CompanyBilling, InvoicingLists} from '../../interfaces/invoicing';
import {NotifierService} from '../../shared/notifier.service';
import {PlaceAddress, streetLine} from '../../controls/places-editor/place-address';
import {companyTabOf, CompanySection, CompanyTabsService, SECTION_FIELDS} from '../company-tab/company-tabs';

/** The tenant's own billing details and invoicing start date (#1516), Admin only. */
// One record, split over the My company tabs by `section` (PR7). Every slice saves the
// whole record: the API writes omitted fields as empty.
@Component({
  selector: 'bst-invoicing-company',
  templateUrl: './invoicing-company.component.html',
  styleUrls: ['../invoicing-client/invoicing-form.scss'],
  standalone: false
})
export class InvoicingCompanyComponent implements OnInit, OnDestroy {
  @Input() section: CompanySection | null = null;
  company: CompanyBilling = null;
  lists: InvoicingLists = null;
  countries: {code: string, name: string}[] = [];
  // The datepicker binds a Moment, the API a Y-m-d string.
  startsOn: Moment = null;
  @ViewChild('startsOnModel') startsOnModel: NgModel;
  errors: Record<string, string[]> = {};
  drafts: BlockingDraft[] = [];
  busy = false;
  private saved: string = null;
  private focusSub: Subscription;
  // Codex I2: load() can be mid-await when the tab is left; both checks below stop it
  // from publishing readiness or subscribing to focus$ for a view that no longer exists.
  private destroyed = false;

  constructor(private invoicing: InvoicingService, private translate: TranslateService, private notifier: NotifierService,
              @Optional() private tabs?: CompanyTabsService) {}

  async ngOnInit() {
    // Codex I1: a save from the tab just left may still be in flight; read after it settles.
    await this.tabs?.pending?.catch(() => null);
    if (this.destroyed) { return; }
    [this.company, this.lists] = await Promise.all([this.invoicing.getCompany(), this.invoicing.getLists()])
      .catch(() => [null, null]);
    if (this.destroyed) { return; }
    if (!this.company) {
      // M4: a slice whose own load failed can never focus the field a readiness link queued for it.
      if (this.section) { this.tabs?.focus$.next(null); }
      return;
    }
    this.countries = countryNames(this.lists.countries, this.translate.currentLang);
    this.startsOn = Globals.fromDateStringToMoment(this.company.invoicing_starts_on);
    this.loaded();
    if (this.section && this.tabs) { this.focusSub = this.tabs.focus$.subscribe(field => this.focusField(field)); }
  }

  ngOnDestroy() { this.destroyed = true; this.focusSub?.unsubscribe(); }

  error(field: string): string | null {
    return fieldError(this.translate, this.errors, field);
  }

  shows(field: string): boolean {
    return !this.section || SECTION_FIELDS[this.section].includes(field);
  }

  // On a tab, a field invoicing still needs is marked where it is filled in.
  warn(field: string): boolean {
    return !!this.section && !!this.company?.missing?.includes('company.' + field);
  }

  status(field: string): string {
    return this.errors[field] ? 'danger' : 'basic';
  }

  // Errors of fields on another tab, with that tab, so equal messages stay told apart.
  otherErrors(): {tab: CompanySection, message: string}[] {
    return Object.keys(this.errors).filter(f => !this.shows(f)).map(f => ({tab: companyTabOf(f), message: this.error(f)}));
  }

  get isDirty(): boolean {
    return !!this.company && this.snapshot() !== this.saved;
  }

  focusField(field: string | null) {
    if (!field || !this.company || !this.shows(field)) { return; }
    this.tabs.focus$.next(null);
    // After this render: the field may have only just appeared.
    setTimeout(() => {
      const el = document.getElementById('company-' + field);
      (el?.querySelector('button') ?? el)?.focus();
    });
  }

  private snapshot(): string {
    return JSON.stringify({...this.company, missing: null, invoicing_starts_on: Globals.toISODate(this.startsOn)});
  }

  private loaded() {
    this.saved = this.snapshot();
    if (this.section) { this.tabs?.missing$.next(this.company.missing ?? []); }
  }

  // A Places pick fills the address once; the fields stay editable and save as today.
  fill(place: PlaceAddress | null) {
    if (!place) { return; }
    this.company.street = streetLine(place);
    this.company.postcode = place.postcode;
    this.company.town = place.town;
    // A country outside the invoicing list is not applied, and counts as missing (Codex I3):
    // the toast must warn, not say filled, when the pick cannot set every field.
    const knownCountry = this.countries.some(c => c.code === place.country);
    if (knownCountry) { this.company.country = place.country; }
    const missing = knownCountry ? place.missing : [...place.missing, 'country'];
    missing.length
      ? this.notifier.notify('places.partial_billing', null, 'warning')
      : this.notifier.notify('places.filled', null, 'success');
  }

  async save() {
    if (this.busy) { return; }
    // A typed date the picker cannot read binds null, which would clear the start date.
    if (this.startsOnModel?.invalid) { this.errors = {invoicing_starts_on: [this.translate.instant('invoicing.error.starts_on')]}; return; }
    this.busy = true;
    this.errors = {};
    this.drafts = [];
    const request = this.invoicing.saveCompany({...this.company, invoicing_starts_on: Globals.toISODate(this.startsOn)})
      .catch(e => { this.errors = fieldErrors(e); this.drafts = e?.error?.drafts ?? []; return null; });
    // Codex I1: the next tab's load() awaits this before it reads, so it never overwrites this save.
    if (this.tabs) { this.tabs.pending = request; }
    const saved = await request;
    this.busy = false;
    if (!saved) { return; }
    this.company = saved;
    this.startsOn = Globals.fromDateStringToMoment(saved.invoicing_starts_on);
    this.loaded();
    // A save that leaves fields missing is no finished setup; the list above names them.
    saved.missing.length ? this.notifier.notify('invoicing.company.saved_incomplete', null, 'warning') : this.notifier.notify('invoicing.company.saved', null, 'success');
  }
}
